import { useEffect, useState } from "react";
import { useLocation, useParams, Link, useNavigate } from "react-router-dom";
import { fetchNewsById, fetchArticleBySlug, enrichNews, fetchTrending } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowUpRight, ArrowLeft, ArrowRight, Clock, Sparkle, Newspaper, LockSimple } from "@phosphor-icons/react";
import { formatInTz, relativeTime } from "@/lib/time";
import GeneratorPanel from "@/components/GeneratorPanel";
import QuotaBadge from "@/components/QuotaBadge";
import SEO, { buildArticleSchema } from "@/components/SEO";

function RecommendedCard({ item }) {
  const [imgFailed, setImgFailed] = useState(false);
  const thumb = item.thumbnail;
  const to = item.slug ? `/news/${item.slug}` : `/article/${item.id}`;
  return (
    <Link
      to={to}
      state={{ item }}
      className="group block bg-white border border-slate-200 hover:border-slate-300 hover:shadow-md rounded-xl overflow-hidden transition-all"
    >
      <div className="relative aspect-[16/9] bg-slate-100 overflow-hidden">
        {!imgFailed && thumb ? (
          <img
            src={thumb}
            alt={item.title}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-300"
            onError={() => setImgFailed(true)}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
            <Newspaper size={28} weight="duotone" className="text-blue-400 opacity-70" />
          </div>
        )}
        {item.category && (
          <div className="absolute top-3 left-3">
            <span className="text-[10px] font-medium px-2 py-0.5 bg-white/90 backdrop-blur text-slate-700 border border-slate-200 rounded capitalize">
              {item.category}
            </span>
          </div>
        )}
      </div>
      <div className="p-4">
        <h3 className="text-sm font-semibold text-slate-900 group-hover:text-blue-600 transition-colors leading-snug line-clamp-2">
          {item.title}
        </h3>
        {item.published && (
          <p className="text-[11px] text-slate-400 mt-1.5 flex items-center gap-1">
            <Clock size={10} weight="bold" /> {relativeTime(item.published)}
          </p>
        )}
      </div>
    </Link>
  );
}

export default function ArticlePage() {
  const { id, slug: slugParam } = useParams();
  const isSlugRoute = !!slugParam;

  const location = useLocation();
  const navigate = useNavigate();
  const { settings, user, setAuthModalOpen } = useAuth();
  const tz = user?.timezone || "UTC";

  const [item, setItem] = useState(location.state?.item || null);
  const [loading, setLoading] = useState(!location.state?.item);
  const [error, setError] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [enriched, setEnriched] = useState(null);
  const [imgFailed, setImgFailed] = useState(false);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    if (item) return;
    let alive = true;
    setLoading(true);
    const fetcher = isSlugRoute ? fetchArticleBySlug(slugParam) : fetchNewsById(id);
    fetcher
      .then((data) => alive && setItem(data))
      .catch((e) => alive && setError(e?.response?.data?.detail || e.message))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [id, slugParam, item, isSlugRoute]);

  useEffect(() => {
    if (!item || isSlugRoute || !item.slug) return;
    navigate(`/news/${item.slug}`, { replace: true, state: { item } });
  }, [item, isSlugRoute, navigate]);

  useEffect(() => {
    if (!item?.id) return;
    let alive = true;
    enrichNews(item.id)
      .then((data) => alive && setEnriched(data))
      .catch(() => {});
    return () => { alive = false; };
  }, [item?.id]);

  useEffect(() => {
    if (!item?.category) return;
    let alive = true;
    fetchTrending(item.category, "US", 1, 6)
      .then((data) => {
        if (!alive) return;
        setRelated((data || []).filter((a) => a.id !== item.id).slice(0, 6));
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [item?.id, item?.category]);

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-16">
        <div className="shimmer h-6 w-32 mb-6 rounded" />
        <div className="shimmer aspect-[16/9] mb-6 rounded-xl" />
        <div className="shimmer h-10 w-3/4 mb-3 rounded" />
      </div>
    );
  }

  if (error || !item) {
    return (
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-20 text-center">
        <h2 className="font-bold text-3xl text-slate-900 mb-3">Article not found</h2>
        <p className="text-slate-500 mb-6">{error || "This article is no longer available."}</p>
        <Link
          to="/"
          className="inline-flex items-center gap-2 border border-slate-200 hover:bg-slate-50 px-5 py-3 text-sm font-medium rounded-lg transition-colors text-slate-700"
          data-testid="back-home-link"
        >
          <ArrowLeft size={14} /> Back to home
        </Link>
      </div>
    );
  }

  const aiDisabled = settings?.ai_enabled === false;
  const imageUrl = (!imgFailed && enriched?.image) || item.thumbnail;
  const richDescription = enriched?.description || item.summary;

  return (
    <>
      <SEO
        title={item.title}
        description={richDescription || item.summary}
        canonical={
          item.slug
            ? `https://trendming.com/news/${item.slug}`
            : `https://trendming.com/article/${item.id}`
        }
        ogImage={imageUrl || undefined}
        ogType="article"
        structuredData={[buildArticleSchema(item)]}
      />

      {/* Breadcrumb */}
      <div className="border-b border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-3">
          <nav aria-label="Breadcrumb">
            <ol className="flex items-center gap-2 text-xs text-slate-400 flex-wrap">
              <li><Link to="/" className="hover:text-slate-700 transition-colors">Home</Link></li>
              <span>/</span>
              <li><Link to="/" className="hover:text-slate-700 transition-colors">News</Link></li>
              {item.category && (
                <>
                  <span>/</span>
                  <li>
                    <Link to={`/category/${item.category}`} className="hover:text-slate-700 transition-colors capitalize">
                      {item.category}
                    </Link>
                  </li>
                </>
              )}
              <span>/</span>
              <li className="text-slate-700 font-medium truncate max-w-[200px]">{item.title}</li>
            </ol>
          </nav>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 md:px-8 py-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

          {/* ── Main content ── */}
          <main className="lg:col-span-2 min-w-0">
            <Link
              to={item.category ? `/category/${item.category}` : "/"}
              className="inline-flex items-center gap-2 text-xs font-medium text-slate-500 hover:text-slate-900 mb-8 transition-colors capitalize"
              data-testid="article-back-link"
            >
              <ArrowLeft size={14} /> Back to {item.category || "news"}
            </Link>

            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <span className="text-[10px] tracking-wide px-2 py-1 bg-slate-100 text-slate-600 border border-slate-200 rounded font-medium capitalize">
                {item.category}
              </span>
              {item.published && (
                <span className="text-xs text-slate-400 flex items-center gap-1" data-testid="article-published-time" title={item.published}>
                  <Clock size={12} weight="bold" /> {formatInTz(item.published, tz)} · {relativeTime(item.published)}
                </span>
              )}
            </div>

            <h1 className="font-bold leading-tight text-3xl md:text-5xl text-slate-900 mb-6" data-testid="article-title">
              {item.title}
            </h1>

            <div className="aspect-[16/9] bg-slate-100 border border-slate-200 mb-8 rounded-xl overflow-hidden">
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt={item.title}
                  className="w-full h-full object-cover"
                  data-testid="article-image"
                  onError={() => setImgFailed(true)}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <div className="shimmer w-full h-full" />
                </div>
              )}
            </div>

            {richDescription && (
              <p className="text-base md:text-lg text-slate-600 leading-relaxed mb-8" data-testid="article-summary">
                {richDescription}
              </p>
            )}

            {user && (
              <div className="border border-slate-200 p-6 md:p-8 bg-slate-50 rounded-xl mb-8" data-testid="source-block">
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                  Source &amp; verification
                </span>
                <div className="mt-3 flex items-center justify-between gap-4 flex-wrap">
                  <div>
                    <div className="font-bold text-xl text-slate-900">{item.source}</div>
                    <p className="text-sm text-slate-500 mt-1">Original publisher · full article on their site</p>
                  </div>
                  <a
                    href={item.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-5 py-3 text-sm font-semibold rounded-lg transition-colors"
                    data-testid="news-source-link"
                  >
                    Read at source <ArrowUpRight size={14} />
                  </a>
                </div>
              </div>
            )}

            {/* Guest gate */}
            {!user && (
              <div className="border border-blue-200 p-6 md:p-8 bg-blue-50 rounded-xl mb-8" data-testid="signin-gate">
                <div className="flex items-start gap-4 flex-wrap">
                  <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center shrink-0 mt-0.5">
                    <LockSimple size={18} weight="fill" color="#fff" />
                  </div>
                  <div className="flex-1 min-w-[240px]">
                    <span className="text-xs font-semibold uppercase tracking-wider text-blue-600">
                      Members only
                    </span>
                    <h3 className="font-bold text-lg md:text-xl mt-1 text-slate-900">
                      Sign in to unlock the full story
                    </h3>
                    <div className="mt-3 flex flex-col gap-2">
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <ArrowUpRight size={13} className="text-slate-400 shrink-0" />
                        Read the original article at {item.source}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <Sparkle size={13} weight="fill" className="text-slate-400 shrink-0" />
                        Generate a script, image prompts &amp; video shot list
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => setAuthModalOpen(true)}
                    className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-5 py-3 text-sm rounded-lg transition-colors inline-flex items-center gap-2 shrink-0"
                    data-testid="signin-gate-button"
                  >
                    Sign in free
                  </button>
                </div>
              </div>
            )}

            {/* Generate Content Kit */}
            {user && (
              <>
                {aiDisabled && (
                  <div className="border border-yellow-300 bg-yellow-50 p-4 text-sm text-yellow-800 mb-6 rounded-lg" data-testid="ai-disabled-banner">
                    AI content generation is currently disabled by the administrator.
                  </div>
                )}

                <div className="mb-4">
                  <QuotaBadge variant="banner" />
                </div>

                <button
                  onClick={() => setPanelOpen(true)}
                  disabled={aiDisabled}
                  className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-white font-semibold px-6 py-4 text-sm rounded-lg transition-colors"
                  data-testid="generate-youtube-script-button"
                >
                  <Sparkle size={16} weight="fill" /> Generate Content Kit
                </button>
              </>
            )}

            {panelOpen && (
              <GeneratorPanel item={item} onClose={() => setPanelOpen(false)} />
            )}

            {/* Bottom back link */}
            <div className="pt-6">
              <Link
                to={item.category ? `/category/${item.category}` : "/"}
                className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-blue-600 transition-colors capitalize"
              >
                <ArrowLeft size={14} /> Back to {item.category || "news"}
              </Link>
            </div>
          </main>

          {/* ── Sidebar ── */}
          <aside className="space-y-5">

            {/* More from category — compact list */}
            {related.length > 0 && (
              <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                <div className="px-5 py-3 bg-slate-50 border-b border-slate-200">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 capitalize">
                    More {item.category} news
                  </h3>
                </div>
                <div className="divide-y divide-slate-100">
                  {related.slice(0, 4).map((rel) => (
                    <Link
                      key={rel.id}
                      to={rel.slug ? `/news/${rel.slug}` : `/article/${rel.id}`}
                      state={{ item: rel }}
                      className="flex items-start gap-3 px-5 py-3.5 hover:bg-slate-50 transition-colors group"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-slate-900 group-hover:text-blue-600 transition-colors leading-snug line-clamp-2">
                          {rel.title}
                        </p>
                      </div>
                      <ArrowRight size={12} className="text-slate-300 group-hover:text-blue-400 flex-shrink-0 transition-colors mt-1" />
                    </Link>
                  ))}
                </div>
                <div className="px-5 py-3 border-t border-slate-100">
                  <Link
                    to={`/category/${item.category}`}
                    className="text-xs font-semibold text-blue-600 hover:underline inline-flex items-center gap-1 capitalize"
                  >
                    Browse all {item.category} news <ArrowRight size={11} />
                  </Link>
                </div>
              </div>
            )}

          </aside>
        </div>

        {/* ── Recommended for You ── */}
        {related.length > 0 && (
          <div className="mt-12 pt-8 border-t border-slate-200">
            <div className="flex items-end justify-between mb-5 flex-wrap gap-3">
              <div>
                <span className="text-xs font-semibold uppercase tracking-wider text-blue-600">/ Recommended</span>
                <h2 className="font-bold text-xl text-slate-900 mt-1">Recommended for You</h2>
              </div>
              {item.category && (
                <Link
                  to={`/category/${item.category}`}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-blue-600 transition-colors capitalize"
                >
                  More {item.category} <ArrowRight size={12} />
                </Link>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {related.map((rel) => (
                <RecommendedCard key={rel.id} item={rel} />
              ))}
            </div>
          </div>
        )}

      </div>
    </>
  );
}
